<template>
  <div class="calculator-page">
    <div class="page-header">
      <div>
        <h1>Коммерческий калькулятор</h1>
        <p>Расчет экономики рейса, ставок перевозчика, тарифа заказчику и субсидирования оплаты.</p>
      </div>
      <div class="header-controls">
        <router-link v-if="canReadSettings" class="settings-link" to="/admin/commercial-calculator-settings">Настройки калькулятора</router-link>
        <div class="status-panel">
          <span :class="{'status-dot': true, 'status-dot-loading': loading}"></span>
          {{ loading ? 'Автопересчет...' : 'Пересчет автоматически через 1 сек.' }}
        </div>
      </div>
    </div>

    <div v-if="isDraftPreview" class="preview-warning">
      <strong>Предпросмотр черновика</strong>
      <span>ID {{ draftId }}<template v-if="draftUpdatedAt"> · изменён {{ formatPreviewDate(draftUpdatedAt) }}</template>. Расчёты не влияют на активные настройки.</span>
    </div>

    <div v-if="isHistoricalPreview" class="preview-warning historical-warning">
      <strong>Историческая версия v{{ historicalConfigVersion }}</strong>
      <span>Расчёты выполняются с архивными настройками и не изменяют активные настройки.</span>
    </div>

    <div v-if="error" class="error-message">{{ error }}</div>

    <div class="layout-grid">
      <section class="card form-card">
        <h2 class="form-subtitle source-title source-general"><i class="source-dot"></i>Общие</h2>
        <div class="form-grid source-fields source-general">
          <label>
            <span class="field-label">Тип клиента<i class="help-tip" title="Влияет на ставку KPI: 1% для обычных, 2% для INBEV/EFES">?</i></span>
            <select v-model="form.client_type">
              <option value="other">Обычный</option>
              <option value="inbev_efes">INBEV/EFES</option>
            </select>
          </label>

          <label>
            <span class="field-label">Зарплата менеджера<i class="help-tip" title="Процент от результата сделки, идущий менеджеру">?</i></span>
            <select v-model.number="form.manager_salary_rate">
              <option v-for="rate in managerSalaryOptions" :key="rate" :value="rate">{{ formatRateLabel(rate) }}</option>
            </select>
          </label>

          <label class="checkbox-label">
            <input v-model="form.apply_min_result_floor" type="checkbox" />
            <span class="field-label">Нижний предел {{ minDealResultLabel }} руб.<i class="help-tip" :title="`Если результат сделки меньше ${minDealResultLabel} ₽, устанавливается ровно ${minDealResultLabel} ₽`">?</i></span>
          </label>
        </div>

        <h2 class="form-subtitle source-title source-customer"><i class="source-dot"></i>Заказчик</h2>
        <div class="form-grid source-fields source-customer">
          <label>
            <span class="field-label">Тариф заказчика<i class="help-tip" title="Сумма, которую платит заказчик за рейс">?</i></span>
            <input
              v-model="priceInputs.customer_price"
              type="text"
              inputmode="decimal"
              @input="updatePriceField('customer_price')"
              @blur="formatPriceField('customer_price')"
            />
          </label>

          <label>
            <span class="field-label">НДС заказчика<i class="help-tip" title="С НДС или без — формат ввода тарифа заказчика">?</i></span>
            <select v-model="form.customer_price_mode">
              <option value="with_vat">С НДС</option>
              <option value="without_vat">Без НДС</option>
            </select>
          </label>

          <label>
            <span class="field-label">Обратный расчёт<i class="help-tip" title="С НДС или без — формат вывода тарифа в таблице обратного расчёта">?</i></span>
            <select v-model="form.output_price_mode">
              <option value="without_vat">Без НДС</option>
              <option value="with_vat">С НДС</option>
            </select>
          </label>
        </div>

        <h2 class="form-subtitle source-title source-carrier"><i class="source-dot"></i>Перевозчик</h2>
        <div class="form-grid source-fields source-carrier">
          <label>
            <span class="field-label">Ставка перевозчика<i class="help-tip" title="Предлагаемая ставка перевозчика за рейс">?</i></span>
            <input
              v-model="priceInputs.carrier_price"
              type="text"
              inputmode="decimal"
              @input="updatePriceField('carrier_price')"
              @blur="formatPriceField('carrier_price')"
            />
          </label>

          <label>
            <span class="field-label">НДС перевозчика<i class="help-tip" title="С НДС или без — ставка НДС перевозчика">?</i></span>
            <select v-model="carrierVatMode">
              <option value="none">Без НДС</option>
              <option v-for="rate in carrierVatOptions" :key="rate" :value="String(rate)">{{ formatRateLabel(rate) }}</option>
            </select>
          </label>
        </div>

        <h2 class="source-title source-subsidy"><i class="source-dot"></i>Субсидирование оплаты</h2>
        <div class="form-grid source-fields source-subsidy">
          <label>
            <span class="field-label">Клиент<i class="help-tip" title="Определяет базовый срок оплаты из справочника клиентов">?</i></span>
            <select v-model="form.subsidy_client">
              <option v-for="client in subsidyClients" :key="client.name" :value="client.name">
                {{ client.name }}
              </option>
            </select>
          </label>

          <label>
            <span class="field-label">Базовая ставка<i class="help-tip" title="Исходная ставка перевозчика, от которой считается удержание">?</i></span>
            <input
              v-model="priceInputs.subsidy_carrier_base_price"
              type="text"
              inputmode="decimal"
              @input="updatePriceField('subsidy_carrier_base_price')"
              @blur="formatPriceField('subsidy_carrier_base_price')"
            />
          </label>

          <label v-if="form.subsidy_client === 'Новый клиент'">
            <span class="field-label">Ручной базовый срок<i class="help-tip" title="Базовый срок оплаты в банковских днях для нового клиента">?</i></span>
            <input v-model.number="form.subsidy_manual_base_days" type="number" min="1" step="1" />
          </label>
        </div>
      </section>

      <section class="card" v-if="result">
        <div class="result-heading">
          <h2>Итог сделки</h2>
          <span v-if="calculationVersion" class="version-badge">v{{ calculationVersion }} · алгоритм {{ calculationAlgorithmVersion || '—' }}</span>
        </div>
        <p class="section-desc">Экономика рейса: прибыль, налоги, KPI, остаток компании</p>
        <div class="metrics-grid">
          <button
            v-for="item in directMetrics"
            :key="item.label"
            class="metric metric-copy"
            :class="{ 'metric-soft': item.highlight }"
            type="button"
            :title="`Скопировать: ${item.copyValue}`"
            @click="copyMetric(item.copyValue)"
          >
            <span class="metric-title-row">
              <span>{{ item.label }}</span>
              <span class="metric-source-dots" aria-hidden="true">
                <i
                  v-for="source in item.sources"
                  :key="source"
                  class="metric-source-dot"
                  :class="`source-${source}`"
                  :title="sourceLabels[source]"
                ></i>
              </span>
            </span>
            <strong>{{ item.value }}</strong>
          </button>
        </div>

        <template v-if="result.payment_subsidy">
          <div class="subsection-header">
            <h3 class="source-title source-subsidy">
              Субсидирование оплаты — {{ result.payment_subsidy.client_name }}
              <i class="source-dot" title="Субсидирование"></i>
            </h3>
            <span>{{ result.payment_subsidy.base_term_label }} · {{ formatNumber(result.payment_subsidy.base_business_days) }} б.д. · {{ formatPercent(result.payment_subsidy.annual_yield) }}</span>
          </div>
          <div class="subsidy-section" :class="{ 'subsidy-loading': loading }">
            <div class="subsidy-spinner" v-if="loading"></div>
            <div class="rates-table-wrap subsidy-table-wrap">
              <table class="rates-table subsidy-rates-table">
                <thead>
                  <tr>
                    <th>Срок оплаты</th>
                    <th class="align-right">Удержание %</th>
                    <th class="align-right">Удержание ₽</th>
                    <th class="align-right">Цена перевозчика</th>
                    <th>Статус</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, i) in result.payment_subsidy.table" :key="i">
                    <td class="strategy-cell">{{ row.term_label }}</td>
                    <td class="rate-value-cell align-right">{{ formatPercent(row.discount_rate) }}</td>
                    <td class="rate-value-cell align-right">{{ formatMoney(row.discount_amount) }}</td>
                    <td class="rate-value-cell align-right rate-result-cell">{{ formatMoney(row.carrier_price_after_discount) }}</td>
                    <td class="subsidy-status-cell">{{ row.approval_status }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>
      </section>
    </div>

    <div class="tables-stack" v-if="result">
      <section class="card rates-card">
        <div class="card-title-row">
          <div>
            <div class="calculation-title">
              <h2>Прямой расчёт: ставки перевозчику</h2>
              <span class="calculation-source-dots" aria-label="Источники: заказчик, перевозчик, общие настройки">
                <i class="source-dot source-customer" title="Заказчик"></i>
                <i class="source-dot source-carrier" title="Перевозчик"></i>
                <i class="source-dot source-general" title="Общие настройки"></i>
              </span>
            </div>
            <p class="section-desc">Рекомендуемые ставки под целевую экономику по сценариям</p>
          </div>
        </div>
        <ResultTable :rows="result.carrier_bids" :show-tariff="false" :show-finance-metrics="canReadFinanceMetrics" />
      </section>

      <section class="card rates-card">
        <div class="card-title-row">
          <div>
            <div class="calculation-title">
              <h2>Обратный расчёт: тариф заказчику</h2>
              <span class="calculation-source-dots" aria-label="Источники: заказчик, перевозчик, общие настройки">
                <i class="source-dot source-customer" title="Заказчик"></i>
                <i class="source-dot source-carrier" title="Перевозчик"></i>
                <i class="source-dot source-general" title="Общие настройки"></i>
              </span>
            </div>
            <p class="section-desc">Рекомендуемый тариф от заданной ставки перевозчика</p>
          </div>
        </div>
        <ResultTable :rows="result.customer_tariffs" :show-tariff="true" :show-finance-metrics="canReadFinanceMetrics" />
      </section>
    </div>
  </div>
</template>

<script>
import { computed, defineComponent, h, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { createToaster } from '@meforma/vue-toaster'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import {
  calculateCommercialCalculator,
  calculateCommercialCalculatorConfigDraft,
  getCommercialCalculatorConfigDraft,
  getCommercialCalculatorRuntimeConfig,
  getCommercialCalculatorConfigVersion,
  getSubsidyClients,
} from '@/backend/commercialCalculatorApi'
import {
  buildCalculatorRequest,
  formatApiErrorDetail,
  normalizeConfigEnvelope,
  normalizeEditableConfig,
} from '@/pages/commercialCalculator/configMapping.mjs'

const LOCAL_STORAGE_KEY = 'commercial-calculator-settings'

const SOURCE_LABELS = {
  general: 'Общие настройки',
  customer: 'Заказчик',
  carrier: 'Перевозчик',
  subsidy: 'Субсидирование',
}

const STRATEGY_LABELS = {
  best: 'Лучшая',
  average: 'Средняя',
  minimal: 'Минимальная',
  bad: 'Плохая',
  worst: 'Худшая',
}

const ResultTable = defineComponent({
  props: {
    rows: { type: Array, required: true },
    showTariff: { type: Boolean, default: false },
    showFinanceMetrics: { type: Boolean, default: false },
  },
  setup(props) {
    return () => {
      const headerCells = [
        h('th', 'Стратегия'),
        h('th', { class: 'align-right' }, props.showTariff ? 'Тариф заказчику' : 'Ставка перевозчика'),
        ...(props.showFinanceMetrics ? [h('th', { class: 'align-right' }, 'Прибыльность')] : []),
        h('th', { class: 'align-right' }, 'ЗП менеджера'),
        h('th', { class: 'align-right' }, 'АВТОЛИДЕР'),
        h('th', { class: 'align-right' }, 'KPI'),
        ...(props.showFinanceMetrics ? [h('th', { class: 'align-right' }, 'Остаток компании')] : []),
        h('th', { class: 'align-right' }, 'Результат'),
      ]
      const colWidths = [
        h('col', { class: 'rate-col-strategy' }),
        h('col', { class: 'rate-col-main' }),
        ...(props.showFinanceMetrics ? [h('col', { class: 'rate-col-percent' })] : []),
        h('col', { class: 'rate-col-money' }),
        h('col', { class: 'rate-col-money' }),
        h('col', { class: 'rate-col-kpi' }),
        ...(props.showFinanceMetrics ? [h('col', { class: 'rate-col-wide' })] : []),
        h('col', { class: 'rate-col-money' }),
      ]
      const buildRowCells = (row) => [
        h('td', { class: 'strategy-cell' }, STRATEGY_LABELS[row.strategy] || row.strategy),
        h('td', { class: 'rate-main-cell' }, [
          h('strong', formatMoney(props.showTariff ? row.customer_tariff : row.carrier_price_gross)),
          props.showTariff ? h('small', `Перевозчик: ${formatMoney(row.carrier_price_gross)}`) : null,
        ]),
        ...(props.showFinanceMetrics ? [h('td', { class: 'rate-value-cell rate-profit-cell' }, formatPercent(row.profitability))] : []),
        h('td', { class: 'rate-value-cell' }, formatMoney(row.manager_salary)),
        h('td', { class: 'rate-value-cell' }, formatMoney(row.autoleader)),
        h('td', { class: 'rate-value-cell' }, formatMoney(row.kpi_amount)),
        ...(props.showFinanceMetrics ? [h('td', { class: 'rate-value-cell' }, formatMoney(row.company_remainder))] : []),
        h('td', { class: 'rate-value-cell rate-result-cell' }, formatMoney(row.deal_result)),
      ]
      return h('div', { class: 'rates-table-wrap' }, [
        h('table', { class: 'rates-table' }, [
          h('colgroup', colWidths),
          h('thead', [h('tr', headerCells)]),
          h('tbody', props.rows.map((row) => h('tr', { key: row.strategy, class: `strategy-row strategy-${row.strategy}` }, buildRowCells(row)))),
        ]),
      ])
    }
  },
})

function formatNumber(value, digits = 2) {
  const number = Number(value || 0)
  return number.toLocaleString('ru-RU', { minimumFractionDigits: digits, maximumFractionDigits: digits })
}

function parsePriceInput(value) {
  const normalized = String(value || '')
    .replace(/\s/g, '')
    .replace(',', '.')
  const number = Number(normalized)
  return Number.isFinite(number) ? number : 0
}

function formatPriceInput(value) {
  return formatNumber(value, 2)
}

function formatMoney(value) {
  return `${formatNumber(value, 2)} ₽`
}

function formatPercent(value) {
  return `${formatNumber(Number(value || 0) * 100, 2)}%`
}

function copyNumber(value, multiplier = 1) {
  const number = Number(value || 0) * multiplier
  return Number.isFinite(number) ? number.toFixed(2) : '0.00'
}

export default {
  name: 'CommercialCalculator',
  components: { ResultTable },
  setup() {
    const route = useRoute()
    const store = useStore()
    const draftId = computed(() => route.params.draftId ? String(route.params.draftId) : '')
    const isDraftPreview = computed(() => Boolean(draftId.value))
    const historicalConfigVersion = computed(() => route.params.configVersion ? String(route.params.configVersion) : '')
    const isHistoricalPreview = computed(() => Boolean(historicalConfigVersion.value))
    const canReadSettings = computed(() => store.getters.hasPermission('calculator.settings.read'))
    const canReadFinanceMetrics = computed(() => store.getters.hasPermission('calculator.finance.read'))
    const draftUpdatedAt = ref(null)
    const loading = ref(false)
    const error = ref('')
    const result = ref(null)
    const throttleTimer = ref(null)
    const requestId = ref(0)
    const toaster = createToaster()
    const carrierVatMode = ref('none')
    const subsidyClients = ref([])
    const activeConfigVersion = ref(null)
    const activeCalculatorConfig = ref(normalizeEditableConfig({}))
    const selectedCalculatorConfig = ref(normalizeEditableConfig({}))
    const storageKey = computed(() => {
      if (isDraftPreview.value) return `${LOCAL_STORAGE_KEY}-preview-${draftId.value}`
      if (isHistoricalPreview.value) return `${LOCAL_STORAGE_KEY}-version-${historicalConfigVersion.value}`
      return LOCAL_STORAGE_KEY
    })
    const form = ref({
      customer_price: 100000,
      customer_price_mode: 'with_vat',
      carrier_price: 74549.18,
      carrier_without_vat: true,
      carrier_vat_rate: null,
      client_type: 'other',
      manager_salary_rate: 0.2,
      output_price_mode: 'without_vat',
      apply_min_result_floor: false,
      subsidy_client: 'Новый клиент',
      subsidy_carrier_base_price: 80000,
      subsidy_manual_base_days: null,
    })
    const priceInputs = ref({
      customer_price: formatPriceInput(form.value.customer_price),
      carrier_price: formatPriceInput(form.value.carrier_price),
      subsidy_carrier_base_price: formatPriceInput(form.value.subsidy_carrier_base_price),
    })

    const decimalOptions = (values, defaults) => {
      const options = (Array.isArray(values) ? values : [])
        .map(Number)
        .filter((value) => Number.isFinite(value) && value >= 0)
      return options.length ? [...new Set(options)] : defaults
    }
    const managerSalaryOptions = computed(() => decimalOptions(selectedCalculatorConfig.value.deal.manager_salary_rates, [0.17, 0.2]))
    const carrierVatOptions = computed(() => decimalOptions(selectedCalculatorConfig.value.deal.carrier_vat_rates, [0.05, 0.07, 0.22]))
    const minDealResultLabel = computed(() => {
      const value = Number(selectedCalculatorConfig.value.deal.min_deal_result)
      const normalized = Number.isFinite(value) ? value : 2500
      return formatNumber(normalized, Number.isInteger(normalized) ? 0 : 2)
    })
    const formatRateLabel = (value) => `${(Number(value || 0) * 100).toLocaleString('ru-RU', { maximumFractionDigits: 2 })}%`

    watch(carrierVatMode, (value) => {
      form.value.carrier_without_vat = value === 'none'
      form.value.carrier_vat_rate = value === 'none' ? null : Number(value)
    }, { immediate: true })

    const loadSettings = () => {
      try {
        const savedSettings = localStorage.getItem(storageKey.value)
        if (savedSettings) {
          const parsed = JSON.parse(savedSettings)
          if (parsed.client_type !== undefined) form.value.client_type = parsed.client_type
          if (parsed.manager_salary_rate !== undefined) form.value.manager_salary_rate = parsed.manager_salary_rate
          if (parsed.apply_min_result_floor !== undefined) form.value.apply_min_result_floor = parsed.apply_min_result_floor
          if (parsed.customer_price !== undefined) form.value.customer_price = parsed.customer_price
          if (parsed.customer_price_mode !== undefined) form.value.customer_price_mode = parsed.customer_price_mode
          if (parsed.output_price_mode !== undefined) form.value.output_price_mode = parsed.output_price_mode
          if (parsed.carrier_price !== undefined) form.value.carrier_price = parsed.carrier_price
          if (parsed.carrier_without_vat !== undefined) form.value.carrier_without_vat = parsed.carrier_without_vat
          if (parsed.carrier_vat_rate !== undefined) form.value.carrier_vat_rate = parsed.carrier_vat_rate
          if (parsed.subsidy_client !== undefined) form.value.subsidy_client = parsed.subsidy_client
          if (parsed.subsidy_carrier_base_price !== undefined) form.value.subsidy_carrier_base_price = parsed.subsidy_carrier_base_price
          if (parsed.subsidy_manual_base_days !== undefined) form.value.subsidy_manual_base_days = parsed.subsidy_manual_base_days
          if (parsed.carrier_without_vat !== undefined) {
            carrierVatMode.value = parsed.carrier_without_vat ? 'none' : String(parsed.carrier_vat_rate)
          }
        }
      } catch (e) {
        console.error('Failed to load settings from localStorage:', e)
      }
    }

    const saveSettings = () => {
      try {
        const settingsToSave = {
          client_type: form.value.client_type,
          manager_salary_rate: form.value.manager_salary_rate,
          apply_min_result_floor: form.value.apply_min_result_floor,
          customer_price: form.value.customer_price,
          customer_price_mode: form.value.customer_price_mode,
          output_price_mode: form.value.output_price_mode,
          carrier_price: form.value.carrier_price,
          carrier_without_vat: form.value.carrier_without_vat,
          carrier_vat_rate: form.value.carrier_vat_rate,
          subsidy_client: form.value.subsidy_client,
          subsidy_carrier_base_price: form.value.subsidy_carrier_base_price,
          subsidy_manual_base_days: form.value.subsidy_manual_base_days,
        }
        localStorage.setItem(storageKey.value, JSON.stringify(settingsToSave))
      } catch (e) {
        console.error('Failed to save settings to localStorage:', e)
      }
    }

    const calculate = async () => {
      const currentRequestId = requestId.value + 1
      requestId.value = currentRequestId
      loading.value = true
      error.value = ''
      try {
        const request = buildCalculatorRequest(form.value, isHistoricalPreview.value ? historicalConfigVersion.value : null)
        const response = isDraftPreview.value
          ? await calculateCommercialCalculatorConfigDraft(draftId.value, request)
          : await calculateCommercialCalculator(request)
        if (currentRequestId === requestId.value) {
          const nested = response?.result?.direct_deal ? response.result : (response?.data?.direct_deal ? response.data : response)
          result.value = {
            ...nested,
            config_version: nested?.config_version ?? response?.config_version,
            algorithm_version: nested?.algorithm_version ?? response?.algorithm_version,
          }
        }
      } catch (err) {
        if (currentRequestId === requestId.value) {
          error.value = formatApiErrorDetail(err.response?.data?.detail, 'Не удалось выполнить расчет')
        }
      } finally {
        if (currentRequestId === requestId.value) {
          loading.value = false
        }
      }
    }

    const scheduleCalculate = () => {
      if (throttleTimer.value) {
        clearTimeout(throttleTimer.value)
      }
      throttleTimer.value = setTimeout(() => {
        throttleTimer.value = null
        calculate()
      }, 1000)
    }

    const updatePriceField = (field) => {
      form.value[field] = parsePriceInput(priceInputs.value[field])
    }

    const formatPriceField = (field) => {
      priceInputs.value[field] = formatPriceInput(form.value[field])
    }

    const directMetrics = computed(() => {
      if (!result.value) return []
      const deal = result.value.direct_deal
      const metrics = [
        { label: 'Тариф заказчика с НДС', value: formatMoney(deal.customer_price_gross), copyValue: copyNumber(deal.customer_price_gross), sources: ['customer'] },
        { label: 'Тариф заказчика без НДС', value: formatMoney(deal.customer_price_net), copyValue: copyNumber(deal.customer_price_net), sources: ['customer'] },
        { label: 'Резерв прибыли', value: formatMoney(deal.profit_reserve), copyValue: copyNumber(deal.profit_reserve), sources: ['customer', 'carrier'], restricted: true },
        { label: 'Ставка перевозчика с НДС', value: formatMoney(deal.carrier_price_gross), copyValue: copyNumber(deal.carrier_price_gross), sources: ['carrier'] },
        { label: 'Ставка перевозчика без НДС', value: formatMoney(deal.carrier_price_net), copyValue: copyNumber(deal.carrier_price_net), sources: ['carrier'] },
        { label: 'Налоговый фонд', value: formatMoney(deal.tax_fund), copyValue: copyNumber(deal.tax_fund), sources: ['customer', 'carrier'], restricted: true },
        { label: 'АВТОЛИДЕР', value: formatMoney(deal.autoleader), copyValue: copyNumber(deal.autoleader), sources: ['customer', 'carrier'], highlight: true },
        { label: 'KPI', value: formatMoney(deal.kpi_amount), copyValue: copyNumber(deal.kpi_amount), sources: ['customer', 'carrier', 'general'], highlight: true },
        { label: 'ЗП менеджера', value: formatMoney(deal.manager_salary), copyValue: copyNumber(deal.manager_salary), sources: ['customer', 'carrier', 'general'], highlight: true },
        { label: 'Прибыльность', value: formatPercent(deal.profitability), copyValue: copyNumber(deal.profitability, 100), sources: ['customer', 'carrier'], restricted: true },
        { label: 'Остаток компании', value: formatMoney(deal.company_remainder), copyValue: copyNumber(deal.company_remainder), sources: ['customer', 'carrier', 'general'], restricted: true },
        { label: 'Ожидаемая комиссия', value: formatMoney(deal.deal_result), copyValue: copyNumber(deal.deal_result), sources: ['customer', 'carrier', 'general'] },
        { label: 'Разница тарифов', value: formatMoney(deal.tariff_difference), copyValue: copyNumber(deal.tariff_difference), sources: ['customer', 'carrier'], restricted: true },
        { label: 'Наценка', value: formatPercent(deal.markup), copyValue: copyNumber(deal.markup, 100), sources: ['customer', 'carrier'], restricted: true },
        { label: 'Маржа', value: formatPercent(deal.margin), copyValue: copyNumber(deal.margin, 100), sources: ['customer', 'carrier'], restricted: true },
      ]
      return canReadFinanceMetrics.value ? metrics : metrics.filter((item) => !item.restricted)
    })

    const calculationVersion = computed(() => result.value?.config_version || null)
    const calculationAlgorithmVersion = computed(() => result.value?.algorithm_version || null)

    const applyCalculatorConfig = (config) => {
      selectedCalculatorConfig.value = normalizeEditableConfig(config)
      if (!managerSalaryOptions.value.includes(Number(form.value.manager_salary_rate))) {
        form.value.manager_salary_rate = managerSalaryOptions.value[0]
      }
      if (carrierVatMode.value !== 'none' && !carrierVatOptions.value.includes(Number(carrierVatMode.value))) {
        carrierVatMode.value = 'none'
      }
      if (isDraftPreview.value || isHistoricalPreview.value) {
        subsidyClients.value = selectedCalculatorConfig.value.clients
          .filter((item) => item.active !== false && item.name)
          .sort((left, right) => Number(left.order || 0) - Number(right.order || 0))
          .map((item) => ({ ...item }))
        if (!subsidyClients.value.some((item) => item.name === form.value.subsidy_client)) {
          form.value.subsidy_client = subsidyClients.value[0]?.name || ''
        }
      }
    }

    const loadCalculatorConfig = async () => {
      if (isDraftPreview.value) {
        try {
          const response = await getCommercialCalculatorConfigDraft(draftId.value)
          const envelope = normalizeConfigEnvelope(response)
          draftUpdatedAt.value = envelope.updated_at
          activeConfigVersion.value = envelope.active_version
          applyCalculatorConfig(envelope.config)
          return true
        } catch (err) {
          const detail = formatApiErrorDetail(err.response?.data?.detail, '')
          error.value = `Черновик не найден, удалён или недоступен.${detail ? ` ${detail}` : ''}`
          return false
        }
      }
      if (isHistoricalPreview.value) {
        try {
          const response = await getCommercialCalculatorConfigVersion(historicalConfigVersion.value)
          applyCalculatorConfig(normalizeConfigEnvelope(response).config)
          return true
        } catch (err) {
          const status = err.response?.status
          const detail = formatApiErrorDetail(err.response?.data?.detail, '')
          const message = status === 403
            ? `Нет доступа к исторической версии v${historicalConfigVersion.value}.`
            : status === 404
              ? `Историческая версия v${historicalConfigVersion.value} не найдена.`
              : `Не удалось загрузить историческую версию v${historicalConfigVersion.value}.`
          error.value = `${message}${detail ? ` ${detail}` : ''}`
          return false
        }
      }
      try {
        const configResponse = await getCommercialCalculatorRuntimeConfig()
        const envelope = normalizeConfigEnvelope(configResponse)
        activeConfigVersion.value = envelope.active_version
        activeCalculatorConfig.value = normalizeEditableConfig(envelope.config)
        applyCalculatorConfig(activeCalculatorConfig.value)
      } catch (e) {
        activeConfigVersion.value = null
        activeCalculatorConfig.value = normalizeEditableConfig({})
        applyCalculatorConfig(activeCalculatorConfig.value)
        return true
      }
      return true
    }

    const copyMetric = async (value) => {
      if (!value && value !== 0) return
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(String(value))
      } else {
        const textArea = document.createElement('textarea')
        textArea.value = String(value)
        textArea.style.position = 'fixed'
        textArea.style.opacity = '0'
        document.body.appendChild(textArea)
        textArea.focus()
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
      }
      toaster.success('Скопировано')
    }
    const formatPreviewDate = (value) => value ? new Date(value).toLocaleString('ru-RU') : '—'

    if (!isDraftPreview.value && !isHistoricalPreview.value) getSubsidyClients().then((data) => { subsidyClients.value = data }).catch(() => {})
    onBeforeUnmount(() => {
      if (throttleTimer.value) {
        clearTimeout(throttleTimer.value)
      }
    })
    onMounted(async () => {
      loadSettings()
      priceInputs.value.customer_price = formatPriceInput(form.value.customer_price)
      priceInputs.value.carrier_price = formatPriceInput(form.value.carrier_price)
      priceInputs.value.subsidy_carrier_base_price = formatPriceInput(form.value.subsidy_carrier_base_price)
      const configLoaded = await loadCalculatorConfig()
      if (!configLoaded) return
      watch(form, scheduleCalculate, { deep: true })
      watch([form, carrierVatMode], saveSettings, { deep: true })
      calculate()
    })

    return {
      carrierVatMode,
      carrierVatOptions,
      activeConfigVersion,
      canReadSettings,
      canReadFinanceMetrics,
      calculationAlgorithmVersion,
      calculationVersion,
      directMetrics,
      draftId,
      draftUpdatedAt,
      error,
      form,
      formatMoney,
      formatNumber,
      formatPercent,
      formatPreviewDate,
      formatPriceField,
      formatRateLabel,
      loading,
      isDraftPreview,
      historicalConfigVersion,
      isHistoricalPreview,
      managerSalaryOptions,
      minDealResultLabel,
      priceInputs,
      result,
      sourceLabels: SOURCE_LABELS,
      subsidyClients,
      calculate,
      copyMetric,
      updatePriceField,
    }
  },
}
</script>

<style scoped>
.calculator-page {
  background: #f5f7fb;
  min-height: 100vh;
  padding: 24px;
}

.page-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}

.page-header h1 {
  color: #1f2937;
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 6px;
}

.page-header p {
  color: #6b7280;
  font-size: 14px;
}

.header-controls {
  align-items: flex-end;
  display: flex;
  gap: 10px;
}

.settings-link {
  background: #fff;
  border: 1px solid #c7d2fe;
  border-radius: 8px;
  color: #4338ca;
  font-size: 13px;
  font-weight: 600;
  padding: 9px 12px;
  text-decoration: none;
}

.status-panel {
  align-items: center;
  background: #fff;
  border: 1px solid #dbeafe;
  border-radius: 999px;
  color: #1d4ed8;
  display: flex;
  font-size: 13px;
  font-weight: 700;
  gap: 8px;
  padding: 10px 14px;
  white-space: nowrap;
}

.status-dot {
  background: #22c55e;
  border-radius: 50%;
  display: block;
  height: 9px;
  width: 9px;
}

.status-dot-loading {
  animation: pulse 1s infinite;
  background: #2563eb;
}

@keyframes pulse {
  0%, 100% { opacity: 0.35; }
  50% { opacity: 1; }
}

.layout-grid {
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(340px, 520px) 1fr;
}

.card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
  margin-bottom: 18px;
  padding: 20px;
}

.card h2 {
  color: #111827;
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 14px;
}

.preview-warning {
  align-items: center;
  background: #fff7ed;
  border: 1px solid #fdba74;
  border-radius: 10px;
  color: #9a3412;
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
  padding: 11px 14px;
}

.preview-warning strong {
  background: #ffedd5;
  border-radius: 999px;
  padding: 5px 9px;
  white-space: nowrap;
}

.preview-warning span {
  font-size: 13px;
}

.historical-warning {
  background: #fef2f2;
  border-color: #fca5a5;
  color: #991b1b;
}

.historical-warning strong {
  background: #fee2e2;
}

.result-heading {
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.version-badge {
  background: #eef2ff;
  border-radius: 999px;
  color: #4338ca;
  font-size: 12px;
  font-weight: 700;
  padding: 6px 10px;
}

.card > h2 + .section-desc {
  margin-top: -14px;
}

.form-subtitle {
  color: #111827;
  font-size: 15px;
  font-weight: 700;
  margin-bottom: 10px;
}

.source-general { --source-color: #22c55e; }
.source-customer { --source-color: #3b82f6; }
.source-carrier { --source-color: #8b5cf6; }
.source-subsidy { --source-color: #f59e0b; }

.source-title {
  align-items: center;
  display: flex;
  gap: 8px;
}

.source-dot,
.metric-source-dot {
  background: var(--source-color);
  border-radius: 50%;
  display: inline-block;
  flex: 0 0 auto;
}

.source-dot {
  height: 9px;
  width: 9px;
}

.card-title-row {
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
}

.card-title-row h2 {
  margin-bottom: 4px;
}

.calculation-title {
  align-items: center;
  display: flex;
  gap: 10px;
  margin-bottom: 4px;
}

.calculation-title h2 {
  margin-bottom: 0;
}

.calculation-source-dots {
  display: inline-flex;
  flex: 0 0 auto;
  gap: 5px;
}

.card-title-row p {
  color: #6b7280;
  font-size: 13px;
  margin: 0;
}

.subsection-header {
  align-items: center;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 18px;
  padding-top: 18px;
}

.subsection-header h3 {
  color: #111827;
  font-size: 16px;
  margin: 0 0 12px;
}

.subsection-header span {
  background: #eff6ff;
  border-radius: 999px;
  color: #1d4ed8;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 12px;
  padding: 6px 10px;
}

.form-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-bottom: 20px;
}

label {
  color: #374151;
  display: flex;
  flex-direction: column;
  font-size: 13px;
  gap: 6px;
}

.field-label {
  align-items: center;
  display: flex;
  gap: 5px;
}

.help-tip {
  align-items: center;
  background: #e5e7eb;
  border-radius: 50%;
  color: #6b7280;
  cursor: help;
  display: inline-flex;
  flex-shrink: 0;
  font-size: 11px;
  font-style: normal;
  font-weight: 700;
  height: 15px;
  justify-content: center;
  line-height: 1;
  width: 15px;
}

.help-tip:hover {
  background: #c7d2fe;
  color: #4338ca;
}

.section-title-row {
  margin-bottom: 12px;
}

.section-title-row + .form-grid {
  margin-bottom: 20px;
}

.section-desc {
  color: #6b7280;
  font-size: 13px;
  font-weight: 400;
  margin: 0 0 14px;
}

input,
select {
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  color: #111827;
  font-size: 14px;
  padding: 10px;
}

.source-fields input:not([type="checkbox"]),
.source-fields select {
  border-left: 4px solid var(--source-color);
}

select,
select option {
  background-color: #fff;
  color: #111827;
}

.checkbox-label {
  align-items: center;
  flex-direction: row;
  grid-column: 1 / -1;
}

.checkbox-label input {
  width: auto;
}

.primary-button {
  background: #2563eb;
  border: none;
  border-radius: 10px;
  color: white;
  cursor: pointer;
  font-weight: 700;
  padding: 12px 18px;
}

.primary-button:disabled {
  cursor: wait;
  opacity: 0.7;
}

.error-message {
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  color: #991b1b;
  margin-bottom: 16px;
  padding: 12px;
}

.metrics-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.metric {
  background: #f9fafb;
  border: 0;
  border-radius: 10px;
  padding: 12px;
  text-align: left;
}

.metric span {
  color: #6b7280;
  display: block;
  font-size: 12px;
  margin-bottom: 5px;
}

.metric .metric-title-row {
  align-items: flex-start;
  display: flex;
  gap: 8px;
  justify-content: space-between;
}

.metric .metric-title-row > span:first-child {
  margin-bottom: 0;
}

.metric .metric-source-dots {
  display: inline-flex;
  flex: 0 0 auto;
  gap: 4px;
  margin: 1px 0 0;
}

.metric-source-dot {
  height: 8px;
  width: 8px;
}

.metric strong {
  color: #111827;
  font-size: 16px;
}

.metric-copy {
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.metric-copy:hover {
  background: #eef4ff;
}

.metric-copy:hover span,
.metric-copy:hover strong {
  color: #1d4ed8;
}

.metric-soft {
  background: #dcfce7;
  border: 1px solid #86efac;
}

.metric-soft span {
  color: #15803d;
}

.metric-soft strong {
  color: #166534;
}

.metric-soft.metric-copy:hover {
  background: #bbf7d0;
}

.metric-soft.metric-copy:hover span,
.metric-soft.metric-copy:hover strong {
  color: #14532d;
}

.tables-stack {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.tables-stack .rates-card {
  margin-bottom: 0;
}

.rates-card {
  overflow: hidden;
  padding: 0;
}

.rates-card .card-title-row {
  padding: 20px 22px 0;
}

.rates-table-wrap {
  overflow-x: auto;
  padding: 0 16px 18px;
  width: 100%;
}

.rates-table-wrap.subsidy-table-wrap {
  padding: 0;
}

.rates-table-wrap :deep(.rates-table) {
  border: 1px solid #e5e7eb;
  border-collapse: separate;
  border-radius: 12px;
  border-spacing: 0;
  min-width: 1040px;
  overflow: hidden;
  table-layout: fixed;
  width: 100%;
}

.rates-table-wrap :deep(.rate-col-strategy) { width: 120px; }
.rates-table-wrap :deep(.rate-col-main) { width: 170px; }
.rates-table-wrap :deep(.rate-col-percent) { width: 110px; }
.rates-table-wrap :deep(.rate-col-money) { width: 135px; }
.rates-table-wrap :deep(.rate-col-kpi) { width: 105px; }
.rates-table-wrap :deep(.rate-col-wide) { width: 155px; }

.rates-table-wrap :deep(.rates-table th),
.rates-table-wrap :deep(.rates-table td) {
  border-bottom: 1px solid #e5e7eb;
  padding: 12px 14px;
  text-align: left;
  vertical-align: middle;
}

.rates-table-wrap :deep(.rates-table th + th),
.rates-table-wrap :deep(.rates-table td + td) {
  border-left: 1px solid #f1f5f9;
}

.rates-table-wrap :deep(.rates-table th) {
  background: #f8fafc;
  color: #475569;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.25;
  white-space: normal;
}

.rates-table-wrap :deep(.rates-table tbody tr:last-child td) {
  border-bottom: none;
}

.rates-table-wrap :deep(.rates-table tbody tr:nth-child(even)) {
  background: #fbfdff;
}

.rates-table-wrap :deep(.rates-table tbody tr:hover) {
  background: #eef4ff;
}

.rates-table-wrap :deep(.strategy-row td:first-child) {
  border-left: 4px solid transparent;
}

.rates-table-wrap :deep(.strategy-cell) {
  color: #0f172a;
  font-size: 14px;
  font-weight: 700;
}

.rates-table-wrap :deep(.strategy-best td:first-child) { border-left-color: #047857; }
.rates-table-wrap :deep(.strategy-average td:first-child) { border-left-color: #2563eb; }
.rates-table-wrap :deep(.strategy-minimal td:first-child) { border-left-color: #7c3aed; }
.rates-table-wrap :deep(.strategy-bad td:first-child) { border-left-color: #d97706; }
.rates-table-wrap :deep(.strategy-worst td:first-child) { border-left-color: #dc2626; }

.rates-table-wrap :deep(.rates-table .align-right),
.rates-table-wrap :deep(.rates-table .rate-main-cell),
.rates-table-wrap :deep(.rates-table .rate-value-cell) {
  text-align: right;
}

.rates-table-wrap :deep(.rate-main-cell strong) {
  color: #111827;
  display: block;
  font-size: 17px;
  line-height: 1.2;
  white-space: nowrap;
}

.rates-table-wrap :deep(.rate-main-cell small) {
  color: #6b7280;
  display: block;
  font-size: 12px;
  margin-top: 4px;
  white-space: nowrap;
}

.rates-table-wrap :deep(.rate-value-cell) {
  color: #111827;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
}

.rates-table-wrap :deep(.rate-profit-cell),
.rates-table-wrap :deep(.rate-result-cell) {
  color: #047857;
}

.subsidy-grid {
  grid-template-columns: repeat(5, minmax(0, 1fr));
}

.rates-table-wrap .subsidy-rates-table {
  min-width: 520px;
}

.rates-table-wrap .sub-col-term { width: 140px; }
.rates-table-wrap .sub-col-percent { width: 100px; }
.rates-table-wrap .sub-col-money { width: 120px; }
.rates-table-wrap .sub-col-status { width: 140px; }

.rates-table-wrap .subsidy-status-cell {
  color: #6b7280;
  font-size: 13px;
  white-space: normal;
}

.subsidy-section {
  position: relative;
}

.subsidy-loading .rates-table-wrap {
  opacity: 0.4;
  pointer-events: none;
  transition: opacity 0.2s;
}

.subsidy-spinner {
  animation: spin 0.7s linear infinite;
  border: 3px solid #dbeafe;
  border-radius: 50%;
  border-top-color: #2563eb;
  height: 32px;
  left: 50%;
  margin: -16px 0 0 -16px;
  position: absolute;
  top: 50%;
  width: 32px;
  z-index: 1;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 1100px) {
  .layout-grid,
  .form-grid,
  .metrics-grid,
  .subsidy-grid {
    grid-template-columns: 1fr;
  }

  .page-header {
    align-items: stretch;
    flex-direction: column;
  }

  .header-controls {
    align-items: stretch;
    flex-direction: column;
  }

  .status-panel {
    justify-content: center;
  }

  .preview-warning {
    align-items: flex-start;
    flex-direction: column;
  }

  .subsection-header {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
